import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Container, Form, Button } from 'react-bootstrap';
import {
  useLoaderData,
  useActionData,
  useNavigation,
  useSubmit,
  useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  stringFieldRequired,
  emailFieldRequired,
} from '@/common/utilities/formFieldSchemas.ts';
import { RegistrationLoaderData } from '../Route.tsx';
import { useFlashMessage } from '@/common/contexts/FlashMessageContext.tsx';

const schema = z.object({
  email: emailFieldRequired,
  firstName: stringFieldRequired('First name'),
  lastName: stringFieldRequired('Last name'),
});

type FormData = z.infer<typeof schema>;

const PasskeyRegistrationForm: React.FC = () => {
  const { email } = useLoaderData() as RegistrationLoaderData;
  const actionData = useActionData();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const { setFlashMessage } = useFlashMessage();

  const isLoading = navigation.state === 'submitting';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email,
      firstName: '',
      lastName: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (actionData?.success) {
      setFlashMessage(actionData.flash.message, actionData.flash.type);
      navigate(`/portal`);
    } else if (actionData?.success == false) {
      setFlashMessage(actionData.flash.message, actionData.flash.type);
    }
  }, [actionData, setFlashMessage, navigate]);

  const onSubmit = (data: FormData) => {
    const formData = new FormData();
    formData.append('email', data.email);
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);

    void submit(formData, {
      method: 'post',
    });
  };

  return (
    <Container className="py-5">
      <h1 className="mb-4">Complete Your Registration</h1>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            disabled
            readOnly
            isInvalid={!!errors.email}
            {...register('email')}
          />
          <Form.Text className="text-muted">
            This email was verified through your invitation link.
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            {errors.email?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            isInvalid={!!errors.firstName}
            {...register('firstName')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.firstName?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            isInvalid={!!errors.lastName}
            {...register('lastName')}
          />
          <Form.Control.Feedback type="invalid">
            {errors.lastName?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Setting up...' : 'Set Up Passkey'}
        </Button>

        <div className="mt-4">
          <h5>What is a passkey?</h5>
          <p className="text-muted">
            Passkeys are a more secure alternative to passwords. They use your
            device's built-in authentication (like fingerprint or face
            recognition) to securely log you in without having to remember
            complex passwords.
          </p>
        </div>
      </Form>
    </Container>
  );
};

export default PasskeyRegistrationForm;
